import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { CustomDialog } from 'components/Dialogs/CustomDialog';
import { WrapperDialogLarge } from 'components/Dialogs/CustomDialog/wrapper';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';
import { IDocument } from 'common/document/interfaces/document';
import { AnimatedLoaderModal } from 'components/Loaders/AnimatedLoader';
import { IRejectReasonDocument } from 'common/request/interfaces/reject-reson-document';
import { FileDTO } from 'store/candidate/interfaces';

import { DOTMedicalCardView } from '../DOTMedicalCardView';
import { DriverLicenseView } from '../DriverLicenseView';
import { IUpdateDocumentImage } from '../DocumentReviewContainer';

import styles from './styles.module.scss';
import { OriginalFileParams } from '../DocumentReviewContainer/types';
import { defaultOriginalFileParams } from '../DocumentReviewContainer/constants';
import { DriverChecklistCardView } from '../DriverChecklistCardView';
import { TooltipEmployeeId } from '../../../../components/TooltipEmployeeId';
import { GenericDocumentView } from '../GenericDocumentView';
import { documentTypeToLabel } from 'functions/documents/documentUtils';
import { listOfDocumentsCanChangeType } from '../../../../common/document/constants/list-of-documents-can-change-type';

const profilePageLabel = 'Profile';

type InputProps = {
  isLoadingVerificationStatus: boolean;
  isLoadingOriginalFile: boolean;
  isLoading: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  candidateFullName: string;
  employeeId?: string;
  activeTabDocumentFile: string;
  setActiveTabDocumentFile: (v: string) => void;
  setOriginalFileParams: (v: OriginalFileParams) => void;
  currentOriginalFile?: FileDTO;
  selectedCandidateDocumentId?: string;
  setSelectedCandidateDocumentId?: (candidateDocumentId: string) => void;
  documents: IDocument[];
  onGoToProfile: () => void;
  onReject: (
    document: IDocument,
    reason: IRejectReasonDocument
  ) => Promise<any>;
  onApprove: (document: IDocument) => Promise<any>;
  onUpdate: (document: IDocument) => Promise<any>;
  onDownload: (document: IDocument) => void;
  onPurge: (document: IDocument) => void;
  onUpdateDocumentImage?: (data: IUpdateDocumentImage) => void;
  onMarkDriverLicenseAsWithoutPhoto?: (
    documentId: string,
    containsUserPhoto: boolean
  ) => void;
};

export const DocumentReviewDialog = ({
  isLoadingVerificationStatus = false,
  isLoadingOriginalFile = false,
  isLoading = false,
  open = false,
  setOpen,
  candidateFullName,
  employeeId,
  activeTabDocumentFile,
  setActiveTabDocumentFile,
  setOriginalFileParams,
  currentOriginalFile,
  selectedCandidateDocumentId,
  setSelectedCandidateDocumentId,
  onGoToProfile,
  onReject,
  onApprove,
  onMarkDriverLicenseAsWithoutPhoto,
  onUpdate,
  documents,
  onDownload,
  onPurge,
  onUpdateDocumentImage,
}: InputProps) => {
  const [containsUserPhoto, setContainsUserPhoto] = React.useState(true);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number>(0);
  const [titlePrev, setTitlePrev] = useState<string>(profilePageLabel);
  const [titleNext, setTitleNext] = useState<string>(
    documentTypeToLabel(documents[0]?.documentType)
  );

  useEffect(() => {
    setCurrentDocumentIndex(0);
  }, [documents]);

  useEffect(() => {
    if (documents[currentDocumentIndex]?.containsUserPhoto === undefined) {
      setContainsUserPhoto(true);
    } else {
      setContainsUserPhoto(
        !!documents[currentDocumentIndex]?.containsUserPhoto
      );
    }
  }, [currentDocumentIndex, documents]);

  useEffect(() => {
    if (!selectedCandidateDocumentId) {
      if (
        documents.length === 1 ||
        currentDocumentIndex === documents.length - 1
      ) {
        setTitleNext(getNavButtonTitle(undefined));
      } else {
        setTitlePrev(getNavButtonTitle(undefined));
        setTitleNext(
          getNavButtonTitle(
            documents?.length && currentDocumentIndex + 1 < documents?.length
              ? documents[currentDocumentIndex + 1].documentType
              : undefined
          )
        );
      }
    } else {
      const index = documents.findIndex(
        (document) =>
          document.candidateDocumentId === selectedCandidateDocumentId
      );
      if (documents.length === 1) {
        setTitlePrev(getNavButtonTitle(undefined));
        setTitleNext('');
      } else if (index === documents.length - 1) {
        setTitlePrev(
          getNavButtonTitle(
            !documents.length ? undefined : documents[index - 1].documentType
          )
        );
        setTitleNext(getNavButtonTitle(undefined));
      } else {
        setTitlePrev(
          index === 0
            ? getNavButtonTitle(undefined)
            : getNavButtonTitle(documents[0].documentType)
        );
        setTitleNext(getNavButtonTitle(documents[index + 1].documentType));
      }
      setCurrentDocumentIndex(index);
    }
  }, [documents, selectedCandidateDocumentId, open, currentDocumentIndex]);

  const resetAndGoToProfile = () => {
    setTitlePrev(profilePageLabel);
    setTitleNext(
      documents.length > 1
        ? documentTypeToLabel(documents[1].documentType)
        : documentTypeToLabel(documents[0].documentType)
    );
    setCurrentDocumentIndex(0);
    setOpen(false);
    onGoToProfile();
  };

  const getNavButtonTitle = (documentType?: DocumentTypeEnum) => {
    if (!documentType) {
      return profilePageLabel;
    }
    return documentTypeToLabel(documentType);
  };

  const handlePrevDocument = () => {
    setOriginalFileParams(defaultOriginalFileParams);
    if (currentDocumentIndex > 0) {
      setTitlePrev(
        getNavButtonTitle(
          currentDocumentIndex - documents.length <= 0
            ? undefined
            : documents[currentDocumentIndex - documents.length].documentType
        )
      );
      setTitleNext(
        getNavButtonTitle(documents[currentDocumentIndex].documentType)
      );
      setCurrentDocumentIndex(currentDocumentIndex - 1);
      setSelectedCandidateDocumentId &&
        setSelectedCandidateDocumentId(
          documents[currentDocumentIndex - 1].candidateDocumentId
        );
    } else {
      resetAndGoToProfile();
    }
  };

  const handleNextDocument = () => {
    setOriginalFileParams(defaultOriginalFileParams);
    const documentsMaxIndex = documents.length - 1;
    if (currentDocumentIndex < documentsMaxIndex) {
      setTitlePrev(
        getNavButtonTitle(documents[currentDocumentIndex].documentType)
      );
      setTitleNext(
        getNavButtonTitle(
          currentDocumentIndex + documents.length >= documentsMaxIndex
            ? undefined
            : documents[currentDocumentIndex + documents.length].documentType
        )
      );
      setCurrentDocumentIndex(currentDocumentIndex + 1);
      setSelectedCandidateDocumentId &&
        setSelectedCandidateDocumentId(
          documents[currentDocumentIndex + 1].candidateDocumentId
        );
    } else {
      resetAndGoToProfile();
    }
  };

  const handleApprove = (details: any) => {
    const doc = documents[currentDocumentIndex];
    if (doc) {
      onApprove({
        ...doc,
        details: details ?? null,
      }).catch((error) => {
        console.error(error);
      });
      if (doc.documentType === DocumentTypeEnum.DRIVER_LICENSE) {
        onMarkDriverLicenseAsWithoutPhoto?.(
          doc.candidateDocumentId,
          containsUserPhoto
        );
      }
    }
  };
  const handleUpdate = (details: any) => {
    if (documents[currentDocumentIndex]) {
      onUpdate({
        ...documents[currentDocumentIndex],
        details,
      }).catch((error) => {
        console.error(error);
      });
    }
  };

  const handleReject = (reason: IRejectReasonDocument) => {
    if (documents[currentDocumentIndex]) {
      onReject(documents[currentDocumentIndex], reason)
        .then(() => {
          setCurrentDocumentIndex(0);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const handleDownload = () => {
    onDownload(documents[currentDocumentIndex]);
  };

  const handlePurge = () => {
    onPurge(documents[currentDocumentIndex]);
  };

  const canChangeDocumentType = useMemo(() => {
    const [d1 = null, d2 = null] = documents.filter((d) =>
      listOfDocumentsCanChangeType.includes(d.documentType)
    );

    return !(d1 && d2 && d1.documentRequestId === d2.documentRequestId);
  }, [documents]);

  return (
    <CustomDialog
      open={open}
      setOpen={setOpen}
      title={
        isLoading ? (
          'Loading...'
        ) : (
          <div className={styles.title}>
            <span>{candidateFullName}</span>
            <TooltipEmployeeId employeeId={employeeId || ''} />
          </div>
        )
      }
      PaperComponent={WrapperDialogLarge}
      classes={{
        root: styles.root,
        dialogTitle: styles.dialogTitle,
        dialogCloseButton: styles.dialogCloseButton,
      }}
    >
      {isLoading && !isLoadingVerificationStatus ? (
        <AnimatedLoaderModal isLoading={isLoading} />
      ) : (
        <>
          {documents[currentDocumentIndex]?.documentType ===
            DocumentTypeEnum.DRIVER_LICENSE && (
            <DriverLicenseView
              isLoadingOriginalFile={isLoadingOriginalFile}
              titlePrev={titlePrev}
              titleNext={titleNext === titlePrev ? '' : titleNext}
              onPrev={handlePrevDocument}
              onNext={handleNextDocument}
              onReject={handleReject}
              onApprove={handleApprove}
              onDownload={handleDownload}
              onPurge={handlePurge}
              onUpdateDocumentImage={onUpdateDocumentImage}
              document={documents[currentDocumentIndex]}
              setOriginalFileParams={setOriginalFileParams}
              currentOriginalFile={currentOriginalFile}
              containsUserPhoto={containsUserPhoto}
              setContainsUserPhoto={setContainsUserPhoto}
            />
          )}
          {documents[currentDocumentIndex]?.documentType ===
            DocumentTypeEnum.DOT_MEDICAL && (
            <DOTMedicalCardView
              isLoadingOriginalFile={isLoadingOriginalFile}
              isLoadingVerificationStatus={isLoadingVerificationStatus}
              titlePrev={titlePrev}
              titleNext={titleNext === titlePrev ? '' : titleNext}
              activeTabDocumentFile={activeTabDocumentFile}
              setActiveTabDocumentFile={setActiveTabDocumentFile}
              setOriginalFileParams={setOriginalFileParams}
              currentOriginalFile={currentOriginalFile}
              onPrev={handlePrevDocument}
              onNext={handleNextDocument}
              onReject={handleReject}
              onApprove={handleApprove}
              onUpdate={handleUpdate}
              onDownload={handleDownload}
              onPurge={handlePurge}
              onUpdateDocumentImage={onUpdateDocumentImage}
              document={documents[currentDocumentIndex]}
            />
          )}
          {documents[currentDocumentIndex]?.documentType ===
            DocumentTypeEnum.DRIVER_CHECKLIST && (
            <DriverChecklistCardView
              isLoadingOriginalFile={isLoadingOriginalFile}
              isLoadingVerificationStatus={isLoadingVerificationStatus}
              titlePrev={titlePrev}
              titleNext={titleNext === titlePrev ? '' : titleNext}
              activeTabDocumentFile={activeTabDocumentFile}
              setActiveTabDocumentFile={setActiveTabDocumentFile}
              setOriginalFileParams={setOriginalFileParams}
              currentOriginalFile={currentOriginalFile}
              onPrev={handlePrevDocument}
              onNext={handleNextDocument}
              onReject={handleReject}
              onApprove={handleApprove}
              onUpdate={handleUpdate}
              onDownload={handleDownload}
              onPurge={handlePurge}
              onUpdateDocumentImage={onUpdateDocumentImage}
              document={documents[currentDocumentIndex]}
              employeeId={employeeId}
              candidateFullName={candidateFullName}
            />
          )}
          {[
            DocumentTypeEnum.RETURN_TO_WORK,
            DocumentTypeEnum.MEDICAL_DISCONTINUANCE,
            DocumentTypeEnum.PHOTO_ID,
          ].includes(documents[currentDocumentIndex]?.documentType) && (
            <GenericDocumentView
              isLoadingOriginalFile={isLoadingOriginalFile}
              isLoadingVerificationStatus={isLoadingVerificationStatus}
              titlePrev={titlePrev}
              titleNext={titleNext === titlePrev ? '' : titleNext}
              activeTabDocumentFile={activeTabDocumentFile}
              setActiveTabDocumentFile={setActiveTabDocumentFile}
              setOriginalFileParams={setOriginalFileParams}
              currentOriginalFile={currentOriginalFile}
              onPrev={handlePrevDocument}
              onNext={handleNextDocument}
              onReject={handleReject}
              onApprove={handleApprove}
              onUpdate={handleUpdate}
              onDownload={handleDownload}
              onPurge={handlePurge}
              onUpdateDocumentImage={onUpdateDocumentImage}
              document={documents[currentDocumentIndex]}
              canChangeDocumentType={canChangeDocumentType}
            />
          )}
        </>
      )}
    </CustomDialog>
  );
};
